/**
 * Service Worker Registration — handles registration, update detection,
 * and forced activation for CW POS offline caching.
 *
 * When a new SW version is detected:
 * - If no active transaction (no cart items), auto-reload to pick up new code
 * - If transaction in progress, defer reload until cart is empty
 */

let pendingUpdate = false;

function hasActiveTransaction() {
  try {
    // Block SW activation during login/sync flow — reloading mid-sync
    // causes the entire sync to restart from 0%
    const path = window.location.pathname;
    if (path === "/login" || path === "/sync" || path === "/open-till" || path === "/open-till-component") {
      return true;
    }

    const cartObj = localStorage.getItem("cartObj");
    if (!cartObj) return false;
    const cart = JSON.parse(cartObj);
    return Array.isArray(cart?.items) && cart.items.length > 0;
  } catch {
    return false;
  }
}

function applyUpdate(registration) {
  if (registration?.waiting) {
    registration.waiting.postMessage({ type: "SKIP_WAITING" });
  }
}

export function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  if (import.meta.env.DEV) return;

  window.addEventListener("load", async () => {
    try {
      const registration = await navigator.serviceWorker.register("./sw.js", {
        scope: "./",
      });

      console.debug("[SW] Registered, scope:", registration.scope);

      setInterval(() => {
        registration.update().catch(() => {});
      }, 30 * 60 * 1000);

      registration.addEventListener("updatefound", () => {
        const newWorker = registration.installing;
        if (!newWorker) return;

        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
            console.debug("[SW] New version ready");

            if (!hasActiveTransaction()) {
              console.debug("[SW] No active transaction — activating new version now");
              applyUpdate(registration);
            } else {
              console.debug("[SW] Transaction in progress — deferring update");
              pendingUpdate = true;
            }
          }
        });
      });

      navigator.serviceWorker.addEventListener("controllerchange", () => {
        const path = window.location.pathname;
        if (path === "/login" || path === "/sync" || path === "/open-till" || path === "/open-till-component") {
          console.debug("[SW] New service worker activated — deferring reload (sync/login in progress)");
          pendingUpdate = false;
          return;
        }
        console.debug("[SW] New service worker activated — reloading");
        window.location.reload();
      });

      navigator.serviceWorker.addEventListener("message", (event) => {
        if (event.data?.type === "SW_UPDATED") {
          console.debug("[SW] Updated to version:", event.data.version);
        }
      });
    } catch (err) {
      console.warn("[SW] Registration failed:", err.message);
    }
  });
}

// Call this after order completion or cart clear to apply deferred updates
export function checkPendingSwUpdate() {
  if (!pendingUpdate) return;
  if (hasActiveTransaction()) return;

  pendingUpdate = false;
  console.debug("[SW] Applying deferred update after transaction complete");
  navigator.serviceWorker?.getRegistration().then((reg) => {
    if (reg?.waiting) {
      applyUpdate(reg);
    }
  });
}
